import { useState } from "react";
import { useLang } from "../contexts/LangContext";
import FadeIn from "../components/FadeIn";

const Kontakt = () => {
    const { t } = useLang();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        projectType: "private",
        message: "",
    });
    const [status, setStatus] = useState("idle");

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            setStatus("error");
            return;
        }
        setStatus("sending");
        // Open mailto with prefilled content
        const subject = encodeURIComponent(
            `[${form.projectType}] Henvendelse fra ${form.name}`,
        );
        const body = encodeURIComponent(
            `Navn: ${form.name}\nE-mail: ${form.email}\nTelefon: ${form.phone}\nProjekttype: ${form.projectType}\n\n${form.message}`,
        );
        window.location.href = `mailto:mm@mmbr.dk?subject=${subject}&body=${body}`;
        setTimeout(() => setStatus("success"), 600);
    };

    return (
        <main data-testid="page-contact" className="bg-white text-[#111111]">
            {/* Hero */}
            <section className="pt-40 md:pt-56 pb-20 md:pb-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-4">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            {t.contact.eyebrow}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-8">
                        <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight whitespace-pre-line">
                            {t.contact.title}
                        </h1>
                        <p className="mt-10 max-w-xl text-base md:text-lg text-[#555] leading-relaxed">
                            {t.contact.intro}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Contact + Form */}
            <section className="py-16 md:py-24 border-t border-[#EAE8E4]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-12 md:gap-6">
                    {/* Info */}
                    <FadeIn className="col-span-12 md:col-span-4 space-y-12">
                        <div>
                            <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                                {t.contact.studio}
                            </div>
                            <p className="font-serif text-xl md:text-2xl leading-snug whitespace-pre-line">
                                {t.contact.studioAddress}
                            </p>
                        </div>
                        <div>
                            <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                                {t.contact.email}
                            </div>
                            <a
                                href="mailto:mm@mmbr.dk"
                                data-testid="contact-email"
                                className="font-serif text-xl md:text-2xl link-underline break-all"
                            >
                                mm@mmbr.dk
                            </a>
                        </div>
                        <div>
                            <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                                {t.contact.phone}
                            </div>
                            <a
                                href="tel:+4529369165"
                                data-testid="contact-phone"
                                className="font-serif text-xl md:text-2xl link-underline"
                            >
                                +45 29 36 91 65
                            </a>
                        </div>
                        <div>
                            <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                                {t.contact.hours}
                            </div>
                            <p className="font-serif text-xl md:text-2xl leading-snug whitespace-pre-line">
                                {t.contact.hoursText}
                            </p>
                        </div>
                    </FadeIn>

                    {/* Form */}
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-7 md:col-start-6">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-8">
                            — {t.contact.formTitle}
                        </div>
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="space-y-10"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                                <div>
                                    <label className="text-[11px] tracking-luxury uppercase text-[#888]">
                                        {t.contact.name}
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        data-testid="form-name"
                                        className="mt-2 w-full bg-transparent border-0 border-b border-[#D9D4CD] focus:border-[#111] focus:ring-0 focus:outline-none py-3 font-serif text-lg transition-colors placeholder:text-[#aaa]"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] tracking-luxury uppercase text-[#888]">
                                        {t.contact.emailField}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                        data-testid="form-email"
                                        className="mt-2 w-full bg-transparent border-0 border-b border-[#D9D4CD] focus:border-[#111] focus:ring-0 focus:outline-none py-3 font-serif text-lg transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] tracking-luxury uppercase text-[#888]">
                                        {t.contact.phoneField}
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={onChange}
                                        data-testid="form-phone"
                                        className="mt-2 w-full bg-transparent border-0 border-b border-[#D9D4CD] focus:border-[#111] focus:ring-0 focus:outline-none py-3 font-serif text-lg transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] tracking-luxury uppercase text-[#888]">
                                        {t.contact.projectType}
                                    </label>
                                    <select
                                        name="projectType"
                                        value={form.projectType}
                                        onChange={onChange}
                                        data-testid="form-project-type"
                                        className="mt-2 w-full bg-transparent border-0 border-b border-[#D9D4CD] focus:border-[#111] focus:ring-0 focus:outline-none py-3 font-serif text-lg transition-colors appearance-none"
                                    >
                                        <option value="private">
                                            {t.contact.projectPrivate}
                                        </option>
                                        <option value="architect">
                                            {t.contact.projectArch}
                                        </option>
                                        <option value="other">
                                            {t.contact.projectOther}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] tracking-luxury uppercase text-[#888]">
                                    {t.contact.message}
                                </label>
                                <textarea
                                    name="message"
                                    value={form.message}
                                    onChange={onChange}
                                    rows="5"
                                    required
                                    data-testid="form-message"
                                    className="mt-2 w-full bg-transparent border-0 border-b border-[#D9D4CD] focus:border-[#111] focus:ring-0 focus:outline-none py-3 font-serif text-lg transition-colors resize-none"
                                />
                            </div>

                            {status === "success" && (
                                <div
                                    data-testid="form-success"
                                    className="text-sm text-[#111] border-l-2 border-[#111] pl-4"
                                >
                                    {t.contact.success}
                                </div>
                            )}
                            {status === "error" && (
                                <div
                                    data-testid="form-error"
                                    className="text-sm text-red-700 border-l-2 border-red-700 pl-4"
                                >
                                    {t.contact.error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={status === "sending"}
                                data-testid="form-submit"
                                className="inline-flex items-center gap-3 px-10 py-4 bg-[#111] text-white hover:bg-[#2A2825] transition-colors text-[11px] tracking-luxury uppercase disabled:opacity-50"
                            >
                                {status === "sending"
                                    ? t.contact.sending
                                    : t.contact.send}
                                <span>→</span>
                            </button>
                        </form>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Kontakt;
